// D130 (§2.2) — where the camera should point when a restart is awarded, and who may use the precision
// goal-aim. Pure: no DOM, no globals, so both the local kickabout and the networked game share it and it
// is testable on its own.
//
// The D44 restart re-orient points the camera at the SPOT, which is right for a throw-in and wrong for
// the one moment aim matters most. The sim already faces a penalty taker at the goal (`phase.ts`), but
// nothing seeded the CAMERA — and the camera IS the aim (`main.ts` feeds `cam.yaw` into `aimYaw`). So
// the render promised an aim the input did not have: you were looking at the ball you were about to
// strike, not at the target.

import type { RestartKind } from '@fafi/shared';

export interface RestartAimInput {
  readonly kind: RestartKind;
  readonly spot: { readonly x: number; readonly y: number };
  /** The designated taker, from the D122 `OUT_OF_PLAY.taker` wire field. */
  readonly taker: number | null;
  /** The entity this client is driving right now. */
  readonly controlled: number | null;
  readonly ownPos: { readonly x: number; readonly y: number };
  /** +1 if the controlled player's team attacks +x. `sign(spot.x)` would do for a PENALTY (the spot is
   *  always in the attacking half) but says nothing about a free kick taken in your own third. */
  readonly attackSign: number;
  readonly pitchLength: number;
}

/** Free kicks farther than this from the goal are build-up, not a shot — keep the spot framing. */
export const GOAL_RANGE_M = 30;

/** The centre of the goal the controlled player is attacking. */
const goalX = (a: RestartAimInput): number => (a.attackSign >= 0 ? 1 : -1) * (a.pitchLength / 2);

/** True when the controlled player is the designated taker of a restart he can shoot from. */
export function isGoalRangeTaker(a: RestartAimInput): boolean {
  if (a.taker === null || a.controlled === null || a.taker !== a.controlled) return false;
  if (a.kind === 'PENALTY') return true;
  if (a.kind !== 'FREE_KICK') return false;
  return Math.hypot(goalX(a) - a.spot.x, a.spot.y) <= GOAL_RANGE_M;
}

/**
 * The yaw the restart camera should ease to: the opponent GOAL for a taker within shooting range, the
 * SPOT for everyone else (the D44 behaviour, unchanged for throw-ins, corners, and the opposition's
 * free kicks). `sign(spot.x)` is unambiguous for a penalty — the spot is always in the attacking half.
 */
export function restartCameraYaw(a: RestartAimInput): number {
  if (!isGoalRangeTaker(a)) return Math.atan2(a.spot.y - a.ownPos.y, a.spot.x - a.ownPos.x);
  return Math.atan2(-a.ownPos.y, goalX(a) - a.ownPos.x);
}
